import React, {useEffect, useState} from 'react';
import {Button, Col, Input, message, Modal, Progress, Row, Table} from 'antd';
import {DeleteOutlined, EditOutlined, HistoryOutlined, MenuOutlined, PlayCircleOutlined} from '@ant-design/icons';
import {DeletionError, TestDto, TestPlanControllerApi, TestPlanDto, TestPlanTestDto} from "../api";
import TestPlanExecutionModal from "../components/modal/testExecution/TestPlanExecutionModal";
import TestPlanCreationModal from "../components/modal/testExecution/TestPlanCreationModal";
import TestPlanDetailsModal from "../components/modal/testExecution/TestPlanDetailsModal";
import {renderStatusAmpel} from "../utils/Utils";
import {useKeycloakHeader} from "../config/Config";
import {useKeycloak} from "@react-keycloak/web";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import MissingAuthorizationPage from "./error/MissingAuthorizationPage";
import {Link} from "react-router-dom";


const TestPlanOverview = () => {
    const {keycloak} = useKeycloak();
    const [testPlanDtos, setTestPlanDtos] = useState<TestPlanDto[]>();
    const [searchText, setSearchText] = useState<string>(''); // Der Suchbegriff
    const [testPlanId, setTestPlanId] = useState<number | null>(null);
    const [testPlanExecutionVisible, setTestPlanExecutionVisible] = useState(false);
    const [selectedTestPlan, setSelectedTestplan] = useState<TestPlanDto>();
    const [testPlanDetailsVisible, setTestPlanDetailsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState<number | null>(null);
    const [progressPercent, setProgressPercent] = useState<number>(0);
    const [modalVisible, setModalVisible] = useState(false);

    const headers = useKeycloakHeader();
    const testPlanControllerApi = new TestPlanControllerApi();


    useEffect(() => {
        fetchData();
    }, [progressPercent])

    useEffect(() => {
        const interval = setInterval(() => {
            if (isLoading !== null) {
                fetchRunStatus();
            }
        }, 2000); // Intervall zur Aktualisierung des Ladebalkens

        return () => clearInterval(interval);
    }, [isLoading]);

    const fetchRunStatus = () => {
        if (isLoading) {
            testPlanControllerApi.getLatestTestPlanExecutionStatus(isLoading, headers).then((axiosResponse) => {
                if (axiosResponse.status === 200) {
                    setProgressPercent(axiosResponse.data);
                }
            })
        }
    }


    const handlePlayButtonClick = (id) => {
        setIsLoading(id);
        testPlanControllerApi.executeTestPlan(id, headers).then((axiosResponse) => {
                setIsLoading(null);

                if (axiosResponse.status === 200) {
                    // Erfolgreiche Antwort
                    message.success('Test wurde erfolgreich ausgeführt!');
                } else if (axiosResponse.status === 206) {
                    // Ressource nicht gefunden
                    let msg = 'Es ist ein Fehler aufgetreten';
                    message.error({
                        content: msg,
                        duration: 30
                    });
                } else if (axiosResponse.status === 500) {
                    // Interner Serverfehler
                    message.error('Interner Serverfehler.');
                } else {
                    // Standardnachricht für andere Fälle
                    message.warning('Unbekannter Fehler aufgetreten.');
                }
            }
        )
    }

    const handleDeleteButton = (testplan: TestPlanDto) => {
        setSelectedTestplan(testplan);
        setModalVisible(true);
    }

    const onModalCancel = () => {
        setModalVisible(false);
    }


    const columns = [
            {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: '5%',
                render: (text, record) => (
                    <span>{renderStatusAmpel(record.latestRunStatus)}</span>
                ),
            },
            {
                title: 'Ausführen',
                key: 'play',
                width: '15%',
                render: (text, record) => (
                    <div>
                        {isLoading === record.id ? (
                            <Progress type="circle" percent={progressPercent} size={30}/>
                        ) : (
                            hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_MANAGER, ROLES.GLOW_TEST_DESIGNER], keycloak) ? (
                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined/>}
                                    loading={isLoading === record.id} // Spinning-Symbol anzeigen, wenn isLoading true ist
                                    onClick={() => handlePlayButtonClick(record.id)}
                                >
                                    Play
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined/>}
                                    disabled // Button deaktiviert, wenn die Rolle nicht passt
                                >
                                    Play
                                </Button>
                            )
                        )}
                    </div>
                ),
            },
            {
                title: 'Ausführungen',
                dataIndex: 'execution',
                key: 'execution',
                width: '5%',
                render: (text, record) => (
                    <Button danger onClick={() => {
                        setTestPlanId(record.id);
                        setTestPlanExecutionVisible(true);
                    }} style={{marginRight: '8px', border: '0px', color: '#1890ff'}}>
                        <HistoryOutlined/>
                    </Button>
                ),
            },
            {
                title: 'Bearbeiten',
                dataIndex: 'edit',
                key: 'edit',
                width: '10%',
                render: (text, record) => (

                    <div>
                        <Button danger onClick={() => {
                            setSelectedTestplan(record);
                            setTestPlanDetailsVisible(true);
                        }} style={{marginRight: '8px', border: '0px', color: '#1890ff'}}>
                            <EditOutlined style={{fontSize: '16px', color: '#1890ff'}}/>
                        </Button>
                        {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DEVELOPER, ROLES.GLOW_TEST_DESIGNER], keycloak) ? (
                            <Button
                                danger
                                onClick={() => handleDeleteButton(record)}
                                style={{marginRight: '8px', border: '0px'}}
                            >
                                <DeleteOutlined/>
                            </Button>
                        ) : (
                            <Button disabled style={{marginRight: '8px', border: '0px'}}>
                                <DeleteOutlined/>
                            </Button>
                        )}
                    </div>
                ),
            }
        ]
    ;

    const updateTestPlan = (testPlanTestDtos: TestPlanTestDto[]) => {
        let currentPlan = selectedTestPlan;
        if (currentPlan && currentPlan.id) {
            currentPlan = {...currentPlan, testPlanTestDtos: testPlanTestDtos}; // Neues Objekt erstellen
            testPlanControllerApi.updateTestPlan(currentPlan, headers).then(response => {
                setSelectedTestplan(undefined);
                message.success("Testplan wurde erfolgreich aktualisiert.");
                fetchData();
            }).catch((error) => {
                message.error("Testplan konnte nicht aktualisiert werden!");
            });
            setTestPlanDetailsVisible(false);
        }
    }


    const handleDeleteTestplan = () => {
        if (selectedTestPlan && selectedTestPlan.id) {
            testPlanControllerApi.deleteTestPlan(selectedTestPlan.id, headers).then(response => {
                    // Erfolgreiche Antwort
                    message.success(selectedTestPlan.name + ' wurde erfolgreich gelöscht!');
                    setModalVisible(false);
                    fetchData();
                }
            ).catch((error) => {
                message.error({content: "Es ist ein unerwarteter Fehler aufgetreten! ", duration: 30});
            })
        }
    }

    const fetchData = () => {
        testPlanControllerApi.getAllTestPlans(headers)
            .then((axiosResponse) => {
                const axiosData: Array<TestPlanDto> = axiosResponse.data;
                setTestPlanDtos(axiosData);
            })
            .catch((error) => {
                console.error('Fehler beim Abrufen der Daten:', error);
            });
    }


    // Funktion zum Filtern der Tabelle basierend auf dem Suchbegriff
    const filteredData = testPlanDtos?.map((plan) => ({
        ...plan,
        key: plan.id,
    })).filter((plan) =>
        plan.name?.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleTestPlanCreated = (value: boolean) => {
        if (value) {
            fetchData();
        }
    }
    let roles = [
        ROLES.GLOW_ADMIN,
        ROLES.GLOW_TEST_MANAGER,
        ROLES.GLOW_TEST_DEVELOPER,
        ROLES.GLOW_TEST_DESIGNER
    ];
    const hasRoleForPage = hasRole(roles, keycloak);
    if (!hasRoleForPage) {
        return (<MissingAuthorizationPage roles={roles}/>);
    }

    return (
        <div>
            <h1>Testpläne</h1>
            <Row>
                {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DESIGNER], keycloak) &&
                    <Col flex={"70px"}>
                      <TestPlanCreationModal onTestPlanCreated={handleTestPlanCreated}/>
                    </Col>}
                <Col flex={"auto"}>
                    <Input
                        placeholder="Nach Namen suchen"
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                    />
                </Col>
            </Row>
            <Table dataSource={filteredData} columns={columns} locale={{emptyText: 'Keine Daten vorhanden'}}/>
            {testPlanId &&
                <TestPlanExecutionModal testPlanId={testPlanId} visible={testPlanExecutionVisible}
                                        onClose={() => setTestPlanExecutionVisible(false)}/>}
            {selectedTestPlan &&
                <TestPlanDetailsModal testPlan={selectedTestPlan} visible={testPlanDetailsVisible}
                                      onClose={() => setTestPlanDetailsVisible(false)}
                                      onUpdateTests={updateTestPlan}/>}
            <Modal
                open={modalVisible}
                title={"Bist du dir sicher?"}
                onCancel={onModalCancel}
                footer={[
                    <Button key="cancel" onClick={onModalCancel}>
                        Nein
                    </Button>,
                    <Button key="save" danger onClick={handleDeleteTestplan}>
                        Ja
                    </Button>,
                ]}
            >
                <span>Willst du wirklich löschen: <b>{selectedTestPlan?.name} ({selectedTestPlan?.id})</b>?</span>
            </Modal>
        </div>
    );
};

export default TestPlanOverview;